import { authAdapter } from './adapter';
import {
  type CurrentUser,
  parseMeResponse,
  sameOriginApiUrl,
} from './api';
import { AuthApiError } from './errors';

/**
 * Who this tab is: the context /api/me gave it (`self`, or `view:<session>` while
 * an admin is logged in as someone). Sent back on every request, so the server
 * can refuse one from a tab whose identity has changed underneath it, such as
 * after Exit in another tab (apps/server/src/impersonation.mjs).
 */
let tabContext: string | undefined;

/** The header the context travels in. */
export const CONTEXT_HEADER = 'X-Mothership-Context';

/** The server's codes for "this tab is no longer who it shows": reload rather than carry on. */
const IDENTITY_CHANGED = new Set(['impersonation_ended', 'context_changed']);

/** Makes an API request with a freshly retrieved session Bearer token. */
export async function authenticatedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const apiUrl = sameOriginApiUrl(input, window.location.origin);

  const token = await authAdapter.getToken();
  if (token === null) {
    throw new AuthApiError(401, 'Your session has ended.');
  }

  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (tabContext !== undefined) headers.set(CONTEXT_HEADER, tabContext);
  const response = await fetch(apiUrl, { ...init, headers });
  if (response.status === 409 && (await identityChanged(response))) {
    // Log in as user ended or began elsewhere (Exit, expiry, another tab): reload, so the
    // app shows who this really is now, rather than carry on unaware.
    window.location.assign('/');
  }
  return response;
}

/** Whether a 409 is the server saying this tab's identity has changed (impersonation.mjs). */
async function identityChanged(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as { code?: unknown };
    return typeof body.code === 'string' && IDENTITY_CHANGED.has(body.code);
  } catch {
    return false;
  }
}

/** Asks the server who this is, and remembers the tab's context. It also answers whether they are allowed in: 403 when not. */
export async function fetchMe(): Promise<CurrentUser> {
  const user = await parseMeResponse(await authenticatedFetch('/api/me'));
  tabContext = user.context;
  return user;
}

